import { apiClient } from './api.client';
import { clearSessionAndRedirect, isTokenExpired } from './api.client';
import { LoginRequest, RegisterRequest, AuthResponse, User, ForgotPasswordRequest, ForgotPasswordResponse, ResetPasswordRequest, PlatformLoginRequest, PlatformAuthResponse, CompanyLookup, LoginMode } from '../types/user.types';

export const authService = {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const headers = credentials.companyId ? { 'X-Company-Id': String(credentials.companyId) } : {};
    const response = await apiClient.post<AuthResponse>('/auth/login', credentials, { headers });
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  async platformLogin(credentials: PlatformLoginRequest): Promise<PlatformAuthResponse> {
    const response = await apiClient.post<PlatformAuthResponse>('/PlatformAuth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('platformToken', response.data.token);
      localStorage.setItem('platformUser', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  /**
   * Companies an account with this username exists in, so the sign-in form can fill the Company
   * field in before the password is entered. Returns [] for an unknown username — the caller must
   * not tell the two apart in the UI. Rate-limited server-side.
   */
  async getCompaniesForUsername(username: string): Promise<CompanyLookup[]> {
    const response = await apiClient.post<CompanyLookup[]>('/auth/companies-for-username', { username });
    return response.data;
  },

  async getPublicCompanies(): Promise<CompanyLookup[]> {
    const response = await apiClient.get<CompanyLookup[]>('/companies/public-list');
    return response.data;
  },

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/register', userData);
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  logout(): void {
    clearSessionAndRedirect();
  },

  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr) as User;
    } catch {
      localStorage.removeItem('user');
      return null;
    }
  },

  isAuthenticated(): boolean {
    if (!localStorage.getItem('authToken')) return false;
    // A present-but-expired token must not count as authenticated — otherwise the
    // root route would land on the Dashboard with a dead session. checkOnLoad()
    // clears it on a hard load; this guards client-side navigations too.
    return !isTokenExpired();
  },

  isPlatformAuthenticated(): boolean {
    return !!localStorage.getItem('platformToken');
  },

  /** True only when the regular authToken JWT carries is_platform=true (set by PlatformAuthService). */
  isMainAppPlatformUser(): boolean {
    const token = localStorage.getItem('authToken');
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload['is_platform'] === 'true' || payload['is_platform'] === true;
    } catch {
      return false;
    }
  },

  getStoredPlatformUser(): import('../types/user.types').PlatformUser | null {
    const str = localStorage.getItem('platformUser');
    if (!str) return null;
    try { return JSON.parse(str); } catch { localStorage.removeItem('platformUser'); return null; }
  },

  platformLogout(): void {
    localStorage.removeItem('platformToken');
    localStorage.removeItem('platformUser');
  },

  updateStoredUser(partial: Partial<import('../types/user.types').User>): void {
    const userStr = localStorage.getItem('user');
    if (!userStr) return;
    try {
      const current = JSON.parse(userStr);
      localStorage.setItem('user', JSON.stringify({ ...current, ...partial }));
    } catch { /* ignore */ }
  },

  async forgotPassword(data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    const response = await apiClient.post<ForgotPasswordResponse>('/auth/forgot-password', data);
    return response.data;
  },

  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    await apiClient.post('/auth/reset-password', data);
  },

  /** Platform-operator variants — same shape, PlatformUsers table, platform mailbox. */
  async platformForgotPassword(usernameOrEmail: string): Promise<string> {
    const res = await apiClient.post<{ message: string }>(
      '/PlatformAuth/forgot-password', { usernameOrEmail });
    return res.data.message;
  },

  async platformResetPassword(token: string, newPassword: string): Promise<string> {
    const res = await apiClient.post<{ message: string }>(
      '/PlatformAuth/reset-password', { token, newPassword });
    return res.data.message;
  },

  async switchMode(targetMode: LoginMode): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/switch-mode', { targetMode });
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
};
